import { Drawer, List, ListItem, ListItemButton, ListItemText, Stack, styled } from '@mui/material';
import { ContainerLayout, HeaderBar } from 'components/index';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLayoutElementsHeight } from 'hooks/useLayoutElementsHeight';
import { DRAWER_WIDTH } from 'themes/constants';
import { DrawerContents } from 'components/Settings/Drawer/DrawerContents';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { SettingsModalContextProvider } from 'contexts/SettingsModalContext/SettingsModalContext';
import { SETTINGS_SECTIONS } from 'features/settings/config/SettingsSections';
import { SETTINGS_SECTION_IDS } from 'features/settings/types/types';
import { useNotificationSettings } from 'features/settings/hooks/useNotificationSettings';

// Usually we avoid dealing with hooks on too much nested level but in Settings its done
// since source of truth are different in each case
// For more read DEVELOPER.md in the root of the project
const ScrollableContainer = styled(Stack)(({ theme }) => {
  const { commonContentHeight } = useLayoutElementsHeight();

  return {
    height: commonContentHeight,
    backgroundColor: theme.palette.brandBackgroundPrimary.main,
    overflowY: 'auto',
    overscrollBehaviorY: 'none',
  };
});

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    position: 'static',
    boxSizing: 'border-box',
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(2, 4),
  '&:hover': {
    backgroundColor: theme.palette.brandBackgroundPrimary.main,
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    fontWeight: 700,
    textDecoration: 'underline',
    textUnderlineOffset: '10px',
    textDecorationThickness: '3px',
    '&:hover': {
      backgroundColor: theme.palette.brandBackgroundPrimary.main,
    },
  },
}));

export const Settings = () => {
  const { isDesktop } = useDeviceType();
  const { isNotificationFlagEnabled, memberSmsEnabled } = useNotificationSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const { translation } = useTranslationContext();

  const currentSection = location.hash.replace('#', '') || SETTINGS_SECTIONS[0].id;
  const flagFilteredSettingsSections = SETTINGS_SECTIONS.filter((section) =>
    section.id === SETTINGS_SECTION_IDS.NOTIFICATIONS
      ? isNotificationFlagEnabled || memberSmsEnabled
      : true,
  );
  const handleSectionClick = (sectionId: string) => {
    navigate(`#${sectionId}`); // Update the URL hash
  };
  return (
    <SettingsModalContextProvider>
      <ContainerLayout>
        <ScrollableContainer>
          <HeaderBar
            heading={translation('checklist_settings_button_voiceover')}
            titleId="settings__title"
          />
          <Stack direction="row" flexGrow={1}>
            {isDesktop && (
              <StyledDrawer variant="permanent" id="settings__drawer" role="complementary">
                <List id="settings__list" aria-label="Settings Sections">
                  {flagFilteredSettingsSections.map((section) => (
                    <ListItem disablePadding key={section.id}>
                      <StyledListItemButton
                        selected={currentSection === section.id}
                        onClick={() => handleSectionClick(section.id)}
                      >
                        <ListItemText
                          disableTypography
                          primary={
                            section.labelTranslationKey
                              ? translation(section.labelTranslationKey)
                              : section.label
                          }
                        />
                      </StyledListItemButton>
                    </ListItem>
                  ))}
                </List>
              </StyledDrawer>
            )}
            <DrawerContents
              isDesktop={isDesktop}
              sections={flagFilteredSettingsSections}
              currentSectionId={currentSection}
            />
          </Stack>
        </ScrollableContainer>
      </ContainerLayout>
    </SettingsModalContextProvider>
  );
};
